import React, { useState, useEffect } from 'react';
import { Field, useFormikContext } from 'formik';

import { InputUI, FormErrorUI, FormLabelUI, AutoCompleteSelectUI, CheckboxUI } from '../Interface';
import { DependenceTypeList } from '../../config/config';
import { IssueMethodList } from '../../config/production';
import warehouseService from '../../service/warehouse.service';
import { generateAutocompleteItems } from '../../utils/helper';

const ProductionSettingFields = ({ viewField, autoCompleteOptions, formData }) => {
  const { values, errors, setFieldValue, validateField } = useFormikContext();
  const [productDefBinOptions, setProductDefBinOptions] = useState([]);

  useEffect(() => {
    if (values.productDefWhs) {
      warehouseService
        .getBins(values.productDefWhs)
        .then((data) => {
          setProductDefBinOptions(generateAutocompleteItems(data ?? [], 'binCode', 'binId'));
        })
        .catch(() => {
          setProductDefBinOptions([]);
        });
    } else {
      setProductDefBinOptions([]);
      setFieldValue('productDefBin', null);
    }
  }, [values.productDefWhs]);

  return (
    <div className="grid grid-cols-6 gap-x-4 mx-4 mb-5">
      <div className="md:col-span-3 xl:col-span-1 col-span-6">
        <Field
          type="autocomplete"
          name="defOperationQtyUnit"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Alapértelmezett művelet mennyiségi egység" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.defOperationQtyUnit} />}
          onChange={(_e, newVal) => {
            setFieldValue('defOperationQtyUnit', newVal?.value ?? null).then(() => {
              validateField('defOperationQtyUnit');
            });
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: values.defOperationQtyUnit !== '' }}
          selectedValue={values.defOperationQtyUnit}
          selectedLabelValue={formData?.operationQtyUnit?.name}
          dataset={autoCompleteOptions.operationQtyUnit}
        />
      </div>
      <div className="md:col-span-2 col-span-4 pt-2">
        <Field
          name="isManufacturedItemInsertable"
          type="checkbox"
          as={CheckboxUI}
          fullWidth
          label={<FormLabelUI text="Forrásjegyzékbe gyártott cikk rögzíthető alapanyagként" />}
          disabled={viewField}
        />
      </div>
      <div className="md:col-span-1 col-span-4 pt-2">
        <Field
          name="operationSequence"
          type="checkbox"
          as={CheckboxUI}
          fullWidth
          label={<FormLabelUI text="Sorrendiség kezelés" />}
          disabled={viewField}
        />
      </div>
      <div className="md:col-span-1 col-span-4 pt-2">
        <Field
          name="conformityStandard"
          type="text"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Megfelelőség szabvány" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.conformityStandard} />}
          InputLabelProps={{ shrink: Boolean(values.conformityStandard) }}
          disabled={viewField}
        />
      </div>
      <div className="md:col-span-1 col-span-4 pt-2">
        <Field
          type="autocomplete"
          name="productionDefIssueMethod"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Kiadás módja" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.productionDefIssueMethod} />}
          onChange={(_e, newVal) => {
            setFieldValue('productionDefIssueMethod', newVal?.value ?? null).then(() => {
              validateField('productionDefIssueMethod');
            });
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: Boolean(values.productionDefIssueMethod) }}
          selectedValue={values.productionDefIssueMethod}
          selectedLabelValue={IssueMethodList.find((item) => item.value === values.productionDefIssueMethod)?.label}
          dataset={IssueMethodList}
        />
      </div>
      <div className="md:col-span-1 col-span-4 pt-2">
        <Field
          type="autocomplete"
          name="materialDefWhs"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Anyag alapértelmezett raktár" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.materialDefWhs} />}
          onChange={(_e, newVal) => {
            setFieldValue('materialDefWhs', newVal?.value ?? null).then(() => {
              validateField('materialDefWhs');
            });
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: Boolean(values.materialDefWhs) }}
          selectedValue={values.materialDefWhs}
          selectedLabelValue={formData?.materialDefWhs}
          dataset={autoCompleteOptions.warehouse}
        />
      </div>
      <div className="md:col-span-1 col-span-4 pt-2">
        <Field
          type="autocomplete"
          name="productDefWhs"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Termék alapértelmezett raktár" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.productDefWhs} />}
          onChange={(_e, newVal) => {
            setFieldValue('productDefWhs', newVal?.value ?? null).then(() => {
              validateField('productDefWhs');
            });
            setFieldValue('productDefBin', null);
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: Boolean(values.productDefWhs) }}
          selectedValue={values.productDefWhs}
          selectedLabelValue={formData?.productDefWhs}
          dataset={autoCompleteOptions.warehouse}
        />
      </div>
      {productDefBinOptions.length > 0 && (
        <div className="md:col-span-1 col-span-4 pt-2">
          <Field
            type="autocomplete"
            name="productDefBin"
            component={AutoCompleteSelectUI}
            fullWidth
            label={<FormLabelUI text="Termék alapértelmezett rakhely" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.productDefBin} />}
            onChange={(_e, newVal) => {
              setFieldValue('productDefBin', newVal?.value ?? null).then(() => {
                validateField('productDefBin');
              });
            }}
            disabled={viewField}
            InputLabelProps={{ shrink: Boolean(values.productDefBin) }}
            selectedValue={values.productDefBin}
            selectedLabelValue={productDefBinOptions.find((b) => b.value === values.productDefBin)?.label ?? ''}
            dataset={productDefBinOptions}
          />
        </div>
      )}
    </div>
  );
};

export default ProductionSettingFields;
